/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Inquiry button click handler - delegates clicks to any element with inquiry data attributes
(function() {
    function isInquiryButton(element) {
        if (!element || !element.dataset || !element.dataset.id) {
            return false;
        }
        const isClickable = element.tagName === 'BUTTON' ||
                           element.tagName === 'A' ||
                           element.getAttribute('role') === 'button' ||
                           element.onclick !== null;
        const hasInquiryAttributes = element.dataset.title ||
                                    element.dataset.sku ||
                                    element.dataset.image ||
                                    element.closest('.shopify-section--apps');
        return isClickable && hasInquiryAttributes;
    }

    document.addEventListener('click', function(e) {
        let button = null;
        let element = e.target;
        while (element && element !== document.body) {
            if (isInquiryButton(element)) {
                button = element;
                break;
            }
            element = element.parentElement;
        }

        if (!button) return;

        const product = {
            id: button.dataset.id,
            title: button.dataset.title || '',
            image: button.dataset.image || '',
            sku: button.dataset.sku || '',
            quantity: parseInt(button.dataset.quantity) || 1,
            url: button.dataset.url || window.location.href
        };

        if (!product.id) {
            console.warn('inquiry button: Product ID is missing', button);
            return;
        }

        // Prevent default immediately to stop navigation/link behavior
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Handle different button types
        if (button.tagName === 'A' && button.href) {
            // If it's a link, remove href temporarily to prevent navigation
            const originalHref = button.href;
            button.href = 'javascript:void(0)';
            // Restore after a short delay
            setTimeout(() => {
                if (button.href === 'javascript:void(0)') {
                    button.href = originalHref;
                }
            }, 100);
        } else if (button.tagName === 'BUTTON') {
            // If it's a button, ensure it doesn't submit a form
            if (button.type === 'submit' || !button.type) {
                button.type = 'button';
            }
            // Find and prevent form submission if button is in a form
            const form = button.closest('form');
            if (form) {
                form.addEventListener('submit', function(evt) {
                    evt.preventDefault();
                    evt.stopPropagation();
                }, { once: true, capture: true });
            }
        }

        // Dispatch the addToInquiry event with bubbles: true to ensure it propagates
        const inquiryEvent = new CustomEvent('addToInquiry', {
            detail: product,
            bubbles: true,
            cancelable: true
        });
        
        console.log('Dispatching addToInquiry event:', product);
        const dispatched = document.dispatchEvent(inquiryEvent);
        
        if (!dispatched) {
            console.warn('addToInquiry event was prevented');
            return;
        }
    }, true);
})();

/**
 * Swish Wishlist Page enhancement.
 * Applies the same B2B logic as product-item.liquid:
 *   - Logged-in customer + priced product → Swish default (add to cart)
 *   - Logged-out OR zero-price product → hide Swish form/price, show inquire_form_btn
 * Runs only on /apps/wishlist (the Swish-rendered wishlist page).
 */
(function() {
    if (!/^\/apps\/wishlist(?:[/?]|$)/.test(location.pathname)) return;

    function extractWishlistCardData(card) {
        var link = card.querySelector('.wk-text-link, a[href*="/products/"]');
        if (!link) return null;
        var href = link.getAttribute('href') || '';
        var handleMatch = href.match(/\/products\/([^?#/]+)/);
        if (!handleMatch) return null;
        var img = card.querySelector('img.wk-image, img');
        var title = (link.textContent || '').trim();
        return {
            handle: handleMatch[1],
            title: title,
            image: img ? (img.getAttribute('src') || '') : '',
            url: href
        };
    }

    function fetchProduct(handle) {
        return fetch('/products/' + handle + '.js')
            .then(function(r) { return r.ok ? r.json() : null; })
            .catch(function() { return null; });
    }

    function injectSkuStock(card, fullProduct) {
        if (card.querySelector('.product-item-meta__sku-stock')) return;
        if (!fullProduct || !fullProduct.variants || !fullProduct.variants.length) return;

        var firstVariant = fullProduct.variants[0];
        var sku = firstVariant.sku || '';
        if (!sku) return;

        /* Mirror snippets/product-item.liquid logic:
           - Only treat as "remote" if at least one variant tracks inventory
             AND no tracked variant has local quantity > 0. Untracked products
             are treated as in-stock. */
        var hasTracked = false;
        var hasLocalStock = false;
        for (var i = 0; i < fullProduct.variants.length; i++) {
            var v = fullProduct.variants[i];
            if (v.inventory_management) {
                hasTracked = true;
                if (v.inventory_quantity > 0) {
                    hasLocalStock = true;
                }
            }
        }
        var inStock = !hasTracked || hasLocalStock;

        var wrapper = document.createElement('p');
        wrapper.className = 'product-item-meta__sku-stock';

        var skuSpan = document.createElement('span');
        skuSpan.className = 'product-item-meta__sku-number';
        skuSpan.textContent = sku;
        wrapper.appendChild(skuSpan);

        var dot = document.createElement('span');
        dot.className = 'product-item-meta__stock-dot' + (inStock ? '' : ' product-item-meta__stock-dot--remote');
        dot.title = inStock ? 'På lager' : 'På fjernlager';
        wrapper.appendChild(dot);

        var meta = card.querySelector('.wk-meta') || card.querySelector('.wk-product-card') || card;
        var priceEl = meta.querySelector('.wk-price');
        if (priceEl && priceEl.parentNode === meta) {
            meta.insertBefore(wrapper, priceEl);
        } else {
            meta.appendChild(wrapper);
        }
    }

    function replaceWithInquiryButton(card, productData, fullProduct) {
        if (card.querySelector('.inquire_form_btn')) return;

        // Hide Swish's add-to-cart form, variant picker, and price
        var form = card.querySelector('.wk-form');
        if (form) form.style.display = 'none';
        var price = card.querySelector('.wk-price');
        if (price) price.style.display = 'none';
        var variants = card.querySelector('.wk-variants');
        if (variants) variants.style.display = 'none';

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'inquire_form_btn button button--primary button--full';
        if (fullProduct && fullProduct.id) button.dataset.id = String(fullProduct.id);
        button.dataset.title = productData.title;
        button.dataset.image = productData.image;
        if (fullProduct && fullProduct.variants && fullProduct.variants[0]) {
            button.dataset.sku = fullProduct.variants[0].sku || '';
        }
        button.dataset.url = productData.url;
        button.dataset.quantity = '1';
        button.textContent = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.inquiryAddToInquiry) || 'Tilføj til forespørgsel';

        var meta = card.querySelector('.wk-meta') || card.querySelector('.wk-product-card') || card;
        meta.appendChild(button);
    }

    function enhance() {
        var cards = document.querySelectorAll('wishlist-product-card');
        if (!cards.length) return;

        var isLoggedIn = typeof window.customerIsLoggedIn !== 'undefined' ? window.customerIsLoggedIn : false;

        cards.forEach(function(card) {
            if (card.dataset.inquiryProcessed) return;

            var productData = extractWishlistCardData(card);
            /* Card may not be fully upgraded yet - retry on next mutation */
            if (!productData) return;

            card.dataset.inquiryProcessed = '1';

            /* Optimistic inquiry replacement for logged-out users so they
               don't briefly see the cart form. Product fetch still runs to
               populate SKU + stock dot + button id. */
            if (!isLoggedIn) {
                replaceWithInquiryButton(card, productData, null);
            }

            fetchProduct(productData.handle).then(function(full) {
                if (!full) return;

                injectSkuStock(card, full);

                /* Backfill inquiry button data (logged-out case) */
                var existingBtn = card.querySelector('.inquire_form_btn');
                if (existingBtn) {
                    existingBtn.dataset.id = String(full.id);
                    if (full.variants && full.variants[0]) {
                        existingBtn.dataset.sku = full.variants[0].sku || '';
                    }
                    return;
                }

                /* Logged-in: decide based on price */
                var priceMax = 0;
                if (full.variants && full.variants.length) {
                    priceMax = Math.max.apply(null, full.variants.map(function(v) {
                        return parseFloat(v.price) || 0;
                    }));
                }
                if (priceMax === 0) {
                    replaceWithInquiryButton(card, productData, full);
                }
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', enhance);
    } else {
        enhance();
    }

    // Swish renders cards as custom elements - watch for late registration
    if (typeof MutationObserver !== 'undefined') {
        var observer = new MutationObserver(function() { enhance(); });
        observer.observe(document.body, { childList: true, subtree: true });
    }
})();

/**
 * Infinite scroll for native collection pages.
 * Observes the pagination element and auto-loads the next page
 * when it enters the viewport, appending products to the grid.
 */
(function() {
  var SELECTOR_PAGINATION = 'page-pagination.pagination';
  var SELECTOR_PRODUCT_LIST = '.product-list__inner';
  var SELECTOR_NEXT_LINK = 'a[rel="next"]';

  function initInfiniteScroll() {
    var pagination = document.querySelector(SELECTOR_PAGINATION);
    if (!pagination) return;

    var nextLink = pagination.querySelector(SELECTOR_NEXT_LINK);
    if (!nextLink) return;

    var nextPageNum = parseInt(nextLink.getAttribute('data-page') || '2');

    // SEO: Pagination nav hidden via CSS (.pagination__nav rule in custom.css)
    // Keeps links in DOM for crawlers

    // Add loading trigger after the hidden nav
    var triggerDiv = document.createElement('div');
    triggerDiv.className = 'infinite-scroll-trigger';
    triggerDiv.style.cssText = 'text-align:center;padding:24px 0;';
    triggerDiv.innerHTML = '<span class="infinite-scroll-spinner"></span>';
    pagination.appendChild(triggerDiv);

    var trigger = triggerDiv;
    var loading = false;
    var currentPage = nextPageNum - 1;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !loading) {
          loadNextPage();
        }
      });
    }, { rootMargin: '400px' });

    observer.observe(trigger);

    function loadNextPage() {
      loading = true;
      currentPage++;

      var url = new URL(window.location.href);
      url.searchParams.set('page', currentPage);

      var facet = document.querySelector('product-facet');
      if (facet) {
        url.searchParams.set('section_id', facet.getAttribute('section-id'));
      }

      fetch(url.toString())
        .then(function(response) { return response.text(); })
        .then(function(html) {
          var tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          var newProducts = tempDiv.querySelector(SELECTOR_PRODUCT_LIST);
          var productList = document.querySelector(SELECTOR_PRODUCT_LIST);

          if (newProducts && productList) {
            var fragment = document.createDocumentFragment();
            Array.from(newProducts.children).forEach(function(child) {
              var clone = child.cloneNode(true);
              // Remove reveal attribute so products are immediately visible
              // (Focal's stagger animation observer doesn't pick up dynamically added items)
              clone.removeAttribute('reveal');
              clone.removeAttribute('reveal-visibility');
              clone.style.opacity = '1';
              fragment.appendChild(clone);
            });
            productList.appendChild(fragment);

            // SEO: Update URL with current page via History API
            var cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.set('page', currentPage);
            history.replaceState({}, '', cleanUrl.toString());

            // SEO: Update pagination nav with new page's pagination
            var newPagination = tempDiv.querySelector(SELECTOR_PAGINATION);
            var newNextLink = newPagination && newPagination.querySelector(SELECTOR_NEXT_LINK);
            var hasMoreProducts = newProducts.children.length > 0;

            if (newPagination) {
              var newNav = newPagination.querySelector('.pagination__nav');
              var currentNav = pagination.querySelector('.pagination__nav');
              if (newNav && currentNav) {
                currentNav.innerHTML = newNav.innerHTML;
              }
            }

            if (newNextLink && hasMoreProducts) {
              loading = false;
            } else {
              // No more pages - hide spinner
              observer.disconnect();
              trigger.remove();
            }
          } else {
            observer.disconnect();
            trigger.style.display = 'none';
          }
        })
        .catch(function() {
          loading = false;
        });
    }
  }

  function tryInit() {
    var pagination = document.querySelector(SELECTOR_PAGINATION);
    if (pagination && pagination.querySelector(SELECTOR_NEXT_LINK)) {
      initInfiniteScroll();
      return true;
    }
    return false;
  }

  // Try immediately, or wait for DOM
  function start() {
    tryInit();

    // Re-init after Focal's AJAX filter/sort re-renders #facet-main
    var facetMain = document.querySelector('#facet-main');
    if (facetMain) {
      new MutationObserver(function() {
        // Small delay to let Focal finish updating DOM
        setTimeout(tryInit, 100);
      }).observe(facetMain, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Alias mellem boejningsformer i soegningen.
 *
 * 'sjækler' og 'sjækel' er samme ord, men Shopify behandler dem som to
 * soegninger med hvert sit resultat. Flertalsformen er den daarlige af de to:
 * otte varegrupper er navngivet i flertal ('Stålsjækler', 'Handelssjækler'),
 * saa 'sjækler' rammer kategorinavne og traekker varer ind der ikke er
 * sjaekler, mens 'sjækel' rammer titlerne.
 *
 * HVORFOR ALIAS OG IKKE SYNONYM
 * Et rigtigt synonym ville sammenlaegge de to resultatsaet og rangere dem som
 * ét. Det kan kun Search & Discovery, ikke temaet. Et alias er det modsatte og
 * enklere: flertalsformen skrives om til entalsformen FOER soegningen koerer,
 * saa resultatet ikke bare ligner — det ER den anden soegning. Prisen er, at
 * varer som kun flertalsformen kunne finde, ikke laengere kan findes med den.
 *
 * MAAL FORSKELLEN FOER DU TILFOEJER ET PAR. Retningen afgoer hvilke varer der
 * forsvinder, og den er ikke den samme for alle ord.
 *   sjækler -> sjækel (maalt 15/9): 375 mod 300 varer, 294 faelles. De 81 som
 *   kun flertallet fandt var naesten alle loefteaag, der matcher paa
 *   beskrivelsen 'kompatibel med sjækler'.
 *   kroge -> krog (maalt 18/9): 488 mod 420 varer, 374 faelles. De 114 som kun
 *   flertallet fandt er loefteaag, taljer, karabinhager og faldliner — varer
 *   der HAR kroge, ikke er kroge. Til gengaeld vinder flertalssoegningen 46
 *   rigtige kroge som kun entalsformen fandt.
 *
 * TRE STEDER, fordi en soegning kan starte tre steder:
 *   1. Formularen — det almindelige tilfaelde.
 *   2. Instant search, der henter med fetch uden om formularen.
 *   3. Direkte links og bogmaerker, der rammer resultatsiden udenom begge.
 * Uden alle tre ville dropdown og resultatside kunne vise hver sit.
 */
(function() {
  'use strict';

  // Boejningsform -> den form der skal soeges paa. Noeglen skal staa med smaa
  // bogstaver; vaerdien er praecis den tekst der sendes til Shopify.
  // Ét par per linje — tabellen er hele reglen.
  var ALIAS = {
    'sjækler': 'sjækel',
    'kroge': 'krog'
  };

  var KANTTEGN = '.,;:!?()[]{}"\'«»';

  // Deler et ord op i tegnsaetning, kerne og tegnsaetning, saa 'sjækler,'
  // stadig rammer tabellen og kommaet overlever.
  function delOp(ord) {
    var start = 0;
    var slut = ord.length;

    while (start < slut && KANTTEGN.indexOf(ord.charAt(start)) !== -1) {
      start++;
    }

    while (slut > start && KANTTEGN.indexOf(ord.charAt(slut - 1)) !== -1) {
      slut--;
    }

    return {
      foer: ord.slice(0, start),
      kerne: ord.slice(start, slut),
      efter: ord.slice(slut)
    };
  }

  // Oversaetter ord for ord, saa 'sjækler g-4163' bliver 'sjækel g-4163' og
  // modelnummeret overlever. Mellemrummene bevares som de blev tastet.
  // Returnerer den oprindelige streng uaendret hvis intet ord var i tabellen,
  // saa kalderen kan sammenligne med === og se om der skete noget.
  function oversaet(term) {
    if (!term) {
      return term;
    }

    var dele = String(term).split(/(\s+)/);
    var aendret = false;

    var ud = dele.map(function(del) {
      if (!del || !del.trim()) {
        return del;
      }

      var d = delOp(del);
      var erstatning = ALIAS[d.kerne.toLowerCase()];

      if (!erstatning) {
        return del;
      }

      aendret = true;
      return d.foer + erstatning + d.efter;
    });

    return aendret ? ud.join('') : term;
  }

  // Bevidst eksponeret, saa reglen kan proeves i konsollen uden at soege:
  //   fkuSoegeAlias.oversaet('Sjækler, G-4163')
  window.fkuSoegeAlias = { oversaet: oversaet };

  // 1. FORMULARER
  // Feltet skrives om lige foer det sendes. Dermed viser baade URL'en og
  // soegefeltet paa resultatsiden den form der faktisk blev soegt paa — vi
  // lader ikke som om vi soegte paa noget andet end vi gjorde.
  document.addEventListener('submit', function(event) {
    var form = event.target;

    if (!form || !form.querySelectorAll) {
      return;
    }

    var felter = form.querySelectorAll('input[name="q"]');

    for (var i = 0; i < felter.length; i++) {
      var ny = oversaet(felter[i].value);

      if (ny !== felter[i].value) {
        felter[i].value = ny;
      }
    }
  }, true);

  // 2. INSTANT SEARCH
  // Dropdownen henter via theme.js' _doPredictiveSearch og _doLiquidSearch.
  // Siden 2026-09-30 oversættes søgeordet DÉR — i grebet om
  // predictive-search-drawer nedenfor (instant search-blokken), som kalder
  // window.fkuSoegeAlias.oversaet — i stedet for i et globalt window.fetch-
  // wrap, som alle sitets kald gik igennem. Samme regel, samme resultat.

  // 3. SIKKERHEDSNET
  // Direkte links, bogmaerker, Googles resultater og "se alle resultater" fra
  // dropdownen rammer resultatsiden udenom formularen. Da koerer serveren den
  // forkerte soegning foerst, og vi sender videre. Det koster en ekstra
  // indlaesning, men kun i de tilfaelde.
  if (window.location.pathname.indexOf('/search') !== -1) {
    try {
      var adresse = new URL(window.location.href);
      var soegt = adresse.searchParams.get('q');
      var maal = soegt === null ? null : oversaet(soegt);

      // Kravet om at maalet oversaetter til sig selv er vagtvaernet mod at to
      // rettelser peger paa hinanden og siden hopper i ring.
      if (maal !== null && maal !== soegt && oversaet(maal) === maal) {
        adresse.searchParams.set('q', maal);

        // Sidetallet skal vaek: resultatsaettet er et andet, og side 7 af den
        // gamle soegning kan vaere tom i den nye.
        adresse.searchParams.delete('page');

        // replace og ikke assign, saa tilbage-knappen ikke havner i en loekke.
        window.location.replace(adresse.toString());
      }
    } catch (e) {
      // Ingen URL-understoettelse eller en adresse vi ikke kan laese.
      // Siden virker, den viser bare flertalsformens resultat.
    }
  }
})();


/**
 * Instant search skal vise det samme som soegesiden — uden at miste fanerne.
 *
 * PROBLEMET
 * Dropdownen hentede fra predictive search-API'et, som udleverer **maks 10
 * varer** uanset hvad man beder om. Vi kan kun sortere det vi faar, saa de 10
 * mindste af API'ets 10 er ikke de 10 mindste af soegningens 301. Resultatet
 * var en raekke der var korrekt sorteret og alligevel saa forkert ud:
 * 1,00T, 4,75T, 6,5T, 12,5T — ingen af de smaa stoerrelser var med.
 *
 * FOERSTE LOESNING OG DENS HUL
 * 2026-09-15 blev dropdownen lagt helt over paa temaets faldback-sti,
 * sections/predictive-search-compatibility.liquid, som henter over den
 * almindelige soegning og sorterer 50 varer. Det gav de rigtige ti varer,
 * men den almindelige soegning kender hverken kollektioner eller
 * soegeforslag. Maalt paa live 2026-09-23 paa 'sjæk', 'løfteå' og 'sjækel':
 * predictive-svaret har fire faner (Produkter, Forslag, Produktkategorier,
 * Sider), kompat-svaret to. Fanerne forsvandt uden at nogen havde bedt om det.
 *
 * LOESNINGEN NU: TO KALD, ÉT SVAR
 * Begge hentes samtidig. Predictive-svaret er skelettet — faner, forslag,
 * kollektioner og sider — og kun dets vareliste skiftes ud med kompat-svarets,
 * som er sorteret over de samme 50 varer som soegesiden. Svartiden er den
 * langsomste af de to, ikke summen, og theme.js afbryder begge naar kunden
 * taster videre, fordi de deler signal.
 *
 * Kompat-kaldet baerer type=product, ligesom soegesiden naar kunden trykker
 * Enter fra dropdownen. Uden det tager sider og artikler pladser blandt de 50
 * — maalt 2026-09-23: op til to per soegning — og dropdownen sorterer et andet
 * udsnit end siden.
 *
 * FALDER ALTID TILBAGE TIL PREDICTIVE-SVARET
 * Fejler kompat-kaldet, eller mangler et af svarene en vareliste, vises
 * predictive-svaret uroert. Det er ogsaa sorteret af samme motor
 * (sections/predictive-search.liquid), blot over API'ets ti i stedet for
 * soegningens 50. Kunden ser aldrig en tom eller oedelagt dropdown paa grund
 * af fletningen — i vaerste fald den dropdown live havde foer.
 *
 * Varesoegning paa delord ('sjæk') og varenummer ('10000821', '1000082')
 * virker paa begge stier — maalt paa live 2026-09-23.
 *
 * Vi skifter sti her frem for i assets/theme.js, som ikke maa redigeres.
 * (Siden 2026-09-30 via et greb om predictive-search-drawer, ikke window.fetch —
 * se kommentaren ved grebet nedenfor.)
 * theme.js finder '#shopify-section-predictive-search' i svaret, og det id
 * har det flettede svar, fordi skelettet er predictive-svaret.
 */
(function() {
  'use strict';

  var KOMPAT = 'predictive-search-compatibility';
  var Q = /[?&]q=([^&]*)/;

  if (typeof window.fetch !== 'function' || typeof DOMParser === 'undefined' || !window.customElements || typeof Map === 'undefined') {
    return;
  }

  function erPredictiveKald(url) {
    if (!url || url.indexOf('section_id=predictive-search') === -1) {
      return false;
    }

    // Faldbacken bruger allerede den rigtige sti; den skal ikke flettes.
    return url.indexOf('section_id=' + KOMPAT) === -1;
  }

  function tilKompat(url) {
    var fundet = url.match(Q);

    if (!fundet) {
      return null;
    }

    var base = '/search';

    if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.searchUrl) {
      base = window.themeVariables.routes.searchUrl;
    }

    // q genbruges kodet som den kom, saa alias-reglen ovenfor stadig naar at
    // rette den paa vej ud — begge kald gaar gennem den.
    return base + '?q=' + fundet[1] + '&type=product&section_id=' + KOMPAT;
  }

  // Skifter predictive-svarets vareliste ud med kompat-svarets. Returnerer
  // null naar et af svarene mangler en vareliste med mindst én vare, saa
  // kalderen viser predictive-svaret uroert.
  function flet(predictiveHtml, kompatHtml) {
    var parser = new DOMParser();
    var skelet = parser.parseFromString(predictiveHtml, 'text/html');
    var kilde = parser.parseFromString(kompatHtml, 'text/html');
    var maal = skelet.querySelector('ul[data-type="products"]');
    var nye = kilde.querySelector('ul[data-type="products"]');

    if (!maal || !nye || !nye.querySelector('li')) {
      return null;
    }

    maal.innerHTML = nye.innerHTML;
    return skelet.body.innerHTML;
  }

  /*
   * 2026-09-30: GREB OM TEMAETS SØGE-ELEMENT I STEDET FOR window.fetch.
   * Tidligere blev hele sitets fetch pakket ind for at fange dropdownens
   * kald. Nu udskiftes kun de to metoder, dropdownen selv bruger
   * (predictive-search-drawer i assets/theme.js). Logikken er den samme:
   * predictive-svaret er skelettet, kompat-svarets sorterede vareliste
   * flettes ind (flet ovenfor, uændret), og alias-reglen gælder begge kald.
   *
   * CACHE PR. SØGEORD (i hukommelsen, kun for siden): samme søgning giver
   * samme flettede svar uden at hente igen — fx når kunden sletter tilbage
   * ('sjækel' -> 'sjæk' -> 'sjækel'). Kun komplette svar gemmes (kompat-
   * kaldet lykkedes); et fejlet svar hentes forfra næste gang. Højst 40
   * søgeord, ældste ud først.
   */
  var CACHE_MAX = 40;
  var cache = new Map();

  function gem(noegle, vaerdi) {
    cache.set(noegle, vaerdi);
    if (cache.size > CACHE_MAX) {
      cache.delete(cache.keys().next().value);
    }
  }

  function alias(term) {
    return window.fkuSoegeAlias ? window.fkuSoegeAlias.oversaet(term) : term;
  }

  function kompatTil(term) {
    var base = '/search';

    if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.searchUrl) {
      base = window.themeVariables.routes.searchUrl;
    }

    return base + '?q=' + encodeURIComponent(term) + '&type=product&section_id=' + KOMPAT;
  }

  window.customElements.whenDefined('predictive-search-drawer').then(function() {
    var proto = window.customElements.get('predictive-search-drawer').prototype;
    var predictiveOrig = proto._doPredictiveSearch;
    var liquidOrig = proto._doLiquidSearch;

    if (typeof predictiveOrig !== 'function') {
      return;
    }

    proto._doPredictiveSearch = function(term) {
      var t = alias(term);
      var noegle = String(t).toLowerCase().replace(/\s+/g, ' ').trim();

      if (cache.has(noegle)) {
        return Promise.resolve(cache.get(noegle));
      }

      var signal = this.abortController ? this.abortController.signal : undefined;

      var kompat = window.fetch(kompatTil(t), signal ? { signal: signal } : undefined)
        .then(function(svar) {
          return svar.ok ? svar.text() : null;
        })
        .catch(function(fejl) {
          // En afbrydelse skal nå frem til theme.js, som selv ignorerer den.
          if (fejl && fejl.name === 'AbortError') {
            throw fejl;
          }
          return null;
        });

      // Markerer afvisningen som håndteret (ingen 'Uncaught (in promise)').
      kompat.catch(function() {});

      return predictiveOrig.call(this, t).then(function(resultat) {
        if (!resultat || !resultat.html) {
          return resultat;
        }

        return kompat.then(function(kompatHtml) {
          var flettet = null;

          try {
            if (kompatHtml !== null) {
              // theme.js giver sektionens INDRE html, som starter med <style>.
              // DOMParser flytter et ledende <style> til <head>, og flet()
              // returnerer body — så forsvandt dropdownens CSS (fundet ved
              // skærmbillede 2026-09-30). En ramme om holder det i body;
              // rammen fjernes igen bagefter. flet() selv er uændret.
              flettet = flet('<div data-fku-ramme>' + resultat.html + '</div>', kompatHtml);

              if (flettet !== null) {
                var holder = document.createElement('div');
                holder.innerHTML = flettet;
                var ramme = holder.querySelector('[data-fku-ramme]');
                flettet = ramme ? ramme.innerHTML : null;
              }
            }
          } catch (e) {
            flettet = null;
          }

          var ud = flettet === null ? resultat : { hasResults: resultat.hasResults, html: flettet };

          if (kompatHtml !== null) {
            gem(noegle, ud);
          }

          return ud;
        });
      });
    };

    if (typeof liquidOrig === 'function') {
      proto._doLiquidSearch = function(term) {
        return liquidOrig.call(this, alias(term));
      };
    }
  });

  // Eksponeret af samme grund som alias-tabellen: reglerne skal kunne proeves
  // i konsollen og i .claude/tests/test-soegning.js uden at soege.
  window.fkuInstantSearch = { tilKompat: tilKompat, erPredictiveKald: erPredictiveKald, flet: flet };
})();


/**
 * FKU søge-opgradering (2026-09-30): seneste søgninger, genbrug af
 * kategori/side-rækken på søgesiden, og målepunkter til dataLayer.
 *
 * Kører EFTER Nicolines alias og instant search-fletning ovenfor og rører
 * ikke fetch. Submit-lytteren her er registreret efter hendes, så feltet
 * allerede er skrevet om (sjækler -> sjækel), når vi læser det.
 *
 * SENESTE SØGNINGER bor i localStorage (kun i kundens egen browser, max 6).
 * Populære søgninger er faste og står i sprogfilerne
 * (search.fku.popular_terms), valgt ud fra Shopify-søgedata 2026-09-30.
 *
 * CACHE TIL SØGESIDEN: dropdownen udsender rækken med kategorier og sider
 * som <template data-fku-ql-cache>. Når kunden trykker Enter, "Se alle
 * resultater" eller "Se alle produkter", gemmes den i sessionStorage under
 * søgeordet, og søgesidens script (main-search) viser den straks.
 *
 * MÅLEPUNKTER: dataLayer-events, som GTM skal have en trigger på for at
 * nå GA4 (Custom Event = fku_search_click / fku_search_add_to_inquiry):
 *   fku_search_target  best_match | shortcut | collection | page | article |
 *                      query | product | view_all_products | popular | recent
 *   fku_search_location dropdown | search_page
 *   fku_search_term    søgeordet
 */
(function() {
  'use strict';

  var RECENT_KEY = 'fku:recent-searches';
  var MAX_RECENT = 6;

  // Storage kan være spærret (privat vindue, blokerede cookies) — så virker
  // alt andet stadig, bare uden hukommelse.
  function lager(navn) {
    try {
      var s = window[navn];
      s.setItem('__fku', '1');
      s.removeItem('__fku');
      return s;
    } catch (e) {
      return null;
    }
  }

  var local = lager('localStorage');
  var session = lager('sessionStorage');

  function ren(q) {
    return String(q || '').replace(/\s+/g, ' ').trim();
  }

  function seneste() {
    if (!local) {
      return [];
    }
    try {
      var a = JSON.parse(local.getItem(RECENT_KEY) || '[]');
      return Array.isArray(a) ? a.filter(function(x) { return typeof x === 'string' && x; }).slice(0, MAX_RECENT) : [];
    } catch (e) {
      return [];
    }
  }

  function husk(q) {
    q = ren(q);
    if (!local || q.length < 2) {
      return;
    }
    var lille = q.toLowerCase();
    var a = seneste().filter(function(x) { return x.toLowerCase() !== lille; });
    a.unshift(q);
    try {
      local.setItem(RECENT_KEY, JSON.stringify(a.slice(0, MAX_RECENT)));
    } catch (e) {}
  }

  function tegnSeneste() {
    var boks = document.querySelector('[data-fku-recent]');
    if (!boks) {
      return;
    }
    var liste = boks.querySelector('[data-fku-recent-list]');
    var a = seneste();
    liste.innerHTML = '';
    a.forEach(function(q) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fku-ql__chip';
      b.setAttribute('data-fku-term', q);
      b.setAttribute('data-fku-track', 'recent');
      b.textContent = q;
      liste.appendChild(b);
    });
    boks.hidden = a.length === 0;
  }

  function gemRaekke() {
    if (!session) {
      return;
    }
    var tpl = document.querySelector('#search-drawer template[data-fku-ql-cache]');
    if (!tpl) {
      return;
    }
    var q = ren(tpl.getAttribute('data-q')).toLowerCase();
    if (!q) {
      return;
    }
    try {
      session.setItem('fku:ql:' + q, tpl.hasAttribute('data-empty') ? '' : tpl.innerHTML.trim());
    } catch (e) {}
  }

  function send(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  function soegeord(el) {
    var drawer = el.closest('#search-drawer');
    var felt = drawer ? drawer.querySelector('input[name="q"]') : document.querySelector('.main-search__input');
    return ren(felt && felt.value);
  }

  // Søgesiden: husk søgeordet (efter alias, så 'sjækler' huskes som 'sjækel').
  if (/\/search\/?$/.test(window.location.pathname)) {
    try {
      var q = new URL(window.location.href).searchParams.get('q');
      if (q) {
        husk(window.fkuSoegeAlias ? window.fkuSoegeAlias.oversaet(q) : q);
      }
    } catch (e) {}
  }

  document.addEventListener('focusin', function(e) {
    if (e.target && e.target.matches && e.target.matches('#search-drawer input[name="q"]')) {
      tegnSeneste();
    }
  });

  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (form && form.id === 'predictive-search-form') {
      var felt = form.querySelector('input[name="q"]');
      if (felt) {
        husk(felt.value);
      }
      gemRaekke();
    }

    if (form && form.action && form.action.indexOf('/cart/add') !== -1 && form.closest('.shopify-section--main-search')) {
      send({ event: 'fku_search_add_to_inquiry', fku_search_location: 'search_page', fku_search_term: soegeord(form) });
    }
  }, true);

  document.addEventListener('click', function(e) {
    var el = e.target && e.target.closest ? e.target.closest('a, button') : null;
    if (!el) {
      return;
    }

    // Populær/seneste søgning: skriv i feltet og lad temaet søge som ved tast.
    var term = el.getAttribute('data-fku-term');
    if (term) {
      var felt = document.querySelector('#search-drawer input[name="q"]');
      if (felt) {
        e.preventDefault();
        felt.value = term;
        felt.dispatchEvent(new Event('input', { bubbles: true }));
        felt.focus();
      }
    }

    if (el.hasAttribute('data-fku-recent-clear')) {
      e.preventDefault();
      if (local) {
        try { local.removeItem(RECENT_KEY); } catch (err) {}
      }
      tegnSeneste();
      return;
    }

    var iDrawer = !!el.closest('#search-drawer');
    var iSoegeside = !!el.closest('.shopify-section--main-search');
    if (!iDrawer && !iSoegeside) {
      return;
    }

    var maal = el.getAttribute('data-fku-track');
    if (!maal && el.tagName === 'A' && el.closest('[data-type="products"], .product-item')) {
      maal = 'product';
    }
    if (!maal) {
      return;
    }

    if (maal === 'view_all_products') {
      husk(soegeord(el));
      gemRaekke();
    }

    send({
      event: 'fku_search_click',
      fku_search_location: iDrawer ? 'dropdown' : 'search_page',
      fku_search_target: maal,
      fku_search_term: maal === 'popular' || maal === 'recent' ? term : soegeord(el),
      fku_search_text: ren(el.textContent).slice(0, 100)
    });
  }, true);
})();

/**
 * FKU 2026-10-01 (nlf): synlig kvittering ved "Tilføj til forespørgsel".
 *
 * Før: varen blev gemt og tælleren i headeren talte op, men intet andet skete.
 * theme.js' addProductToInquiry() viser `document.querySelector('.cart-notification')`,
 * og det første element med den klasse er temaets tomme, skjulte kurv-toast
 * (<cart-notification global hidden> i theme.liquid), så beskeden blev aldrig synlig
 * — hverken fra produktkort eller produktsiden.
 *
 * Nu: addProductToInquiry() (global funktion i theme.js, som BÅDE kort-knapperne via
 * addToInquiry-eventet og produktsidens #inquiry-button kalder) pakkes ind. Selve
 * gemningen kører uændret først; bagefter: (1) knappen viser "Tilføjet!" i 2 s,
 * (2) en grøn bjælke lige under headeren med varens titel og et link til
 * forespørgslen med antal, (3) tælleren i headeren "hopper" kort.
 * Tekster: themeVariables.strings.inquiryAdded + headerens eget forespørgsels-link
 * (label og URL er allerede oversat og peger på det rigtige marked/sprog).
 */
(function () {
  if (typeof window.addProductToInquiry !== 'function') return;
  var original = window.addProductToInquiry;
  var lastBtn = null, lastBtnAt = 0, toast = null, hideTimer = null;

  // Husk hvilken knap der blev trykket på (kort: .inquire_form_btn, PDP: #inquiry-button).
  // På WINDOW i capture-fasen: kort-knappernes egen lytter (document, capture) kalder
  // stopImmediatePropagation, så en lytter på document ville aldrig se klikket.
  window.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('.inquire_form_btn, #inquiry-button');
    if (b) { lastBtn = b; lastBtnAt = Date.now(); }
  }, true);

  function strings() {
    return (window.themeVariables && window.themeVariables.strings) || {};
  }

  function count() {
    try {
      return (JSON.parse(localStorage.getItem('inquireList')) || []).reduce(function (t, i) {
        return t + (parseInt(i.quantity, 10) || 1);
      }, 0);
    } catch (e) { return 0; }
  }

  function headerLink() {
    var bubble = document.querySelector('[js-inquire-bubble]');
    return bubble && bubble.closest('a');
  }

  function markButton(btn) {
    if (!btn || btn.classList.contains('fku-inq-added')) return;
    var html = btn.innerHTML;
    btn.classList.add('fku-inq-added');
    // hårdt mellemrum: kort-knappen er flex, og et almindeligt mellemrum i kanten forsvinder
    btn.innerHTML = '<span aria-hidden="true">✓ </span>' + (strings().inquiryAdded || 'Tilføjet!');
    setTimeout(function () {
      btn.innerHTML = html;
      btn.classList.remove('fku-inq-added');
    }, 2000);
  }

  function bumpBubble() {
    var bubble = document.querySelector('[js-inquire-bubble]');
    if (!bubble) return;
    bubble.classList.remove('fku-inq-bump');
    void bubble.offsetWidth; // genstart animationen
    bubble.classList.add('fku-inq-bump');
  }

  function headerBottom() {
    // .site-header ruller væk med siden; er den ude af syne, lægges bjælken øverst (0)
    var h = document.querySelector('header.site-header');
    var b = h ? h.getBoundingClientRect().bottom : 0;
    return Math.max(0, Math.round(b));
  }

  function showToast(product) {
    var link = headerLink();
    var label = link ? (link.querySelector('.inquire__label') || link).textContent.trim() : '';
    var url = link ? link.getAttribute('href') : '/pages/inquire';
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fku-inq-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.hidden = true;
      document.body.appendChild(toast);
      toast.addEventListener('mouseenter', function () { clearTimeout(hideTimer); });
      toast.addEventListener('mouseleave', function () { hideTimer = setTimeout(hide, 2500); });
      toast.addEventListener('click', function (e) {
        if (e.target.closest('[data-fku-inq-close]')) hide();
      });
    }
    var esc = function (s) { var d = document.createElement('div'); d.textContent = s || ''; return d.innerHTML; };
    toast.innerHTML =
      '<div class="container"><div class="fku-inq-toast__inner">' +
        '<svg focusable="false" width="20" height="20" viewBox="0 0 20 20" class="fku-inq-toast__icon" aria-hidden="true">' +
          '<rect width="20" height="20" rx="10" fill="currentColor"></rect>' +
          '<path d="M6 10L9 13L14 7" fill="none" stroke="#28af5f" stroke-width="2"></path></svg>' +
        '<div class="fku-inq-toast__text">' +
          '<span class="fku-inq-toast__heading">' + esc(strings().inquiryAdded || 'Tilføjet!') + '</span> ' +
          '<span class="fku-inq-toast__title">' + esc(product && product.title) + '</span>' +
        '</div>' +
        '<a class="fku-inq-toast__link" href="' + esc(url) + '">' + esc(label || 'Din forespørgsel') + ' (' + count() + ') →</a>' +
        '<button type="button" class="fku-inq-toast__close" data-fku-inq-close aria-label="' + esc(strings().accessibilityClose || 'Luk') + '">' +
          '<svg focusable="false" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path></svg>' +
        '</button>' +
      '</div></div>';
    toast.style.top = headerBottom() + 'px';
    toast.hidden = false;
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, 5000);
  }

  function hide() {
    if (!toast) return;
    clearTimeout(hideTimer);
    toast.classList.remove('is-visible');
    setTimeout(function () { if (!toast.classList.contains('is-visible')) toast.hidden = true; }, 250);
  }

  window.addEventListener('pagehide', hide);

  window.addProductToInquiry = function (product) {
    var result = original.apply(this, arguments);
    try {
      if (product && product.id) {
        if (Date.now() - lastBtnAt < 1500) markButton(lastBtn);
        bumpBubble();
        showToast(product);
      }
    } catch (e) { /* kvitteringen må aldrig stoppe selve tilføjelsen */ }
    return result;
  };
})();

/**
 * FKU 2026-10-01 (nlf): temaets "Varen er tilføjet din kurv!"-besked placeres som
 * forespørgsels-beskeden — lige UNDER headeren (eller øverst, når headeren er rullet
 * væk), så logo, søgning og kurv-ikonet ikke dækkes. Før lå den fast på top: 0
 * (FKU-90-reglen i custom.css). Temaet viser beskeden ved at fjerne [hidden]; vi
 * sætter blot top i samme øjeblik. Indhold, tider og luk-knap er temaets egne.
 */
(function () {
  var n = document.querySelector('cart-notification[global]');
  if (!n || typeof MutationObserver === 'undefined') return;
  function place() {
    // attributten, ikke n.hidden: temaets element har kun en setter, så getteren er undefined
    if (n.hasAttribute('hidden')) return;
    var h = document.querySelector('header.site-header');
    var b = h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
    n.style.top = b + 'px';
  }
  new MutationObserver(place).observe(n, { attributes: true, attributeFilter: ['hidden'] });
})();
